import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  ThemeProvider,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { createTheme } from "@mui/material/styles";
import { styled } from "@mui/system";
import siteTheme from "../../theme/theme";
import Marquee from "react-fast-marquee";
import CloudIcon from "@mui/icons-material/Cloud";
import FilterDramaIcon from "@mui/icons-material/FilterDrama";
import GrainIcon from "@mui/icons-material/Grain";
import ThunderstormIcon from "@mui/icons-material/Thunderstorm";
import UmbrellaIcon from "@mui/icons-material/Umbrella";
import WbSunnyIcon from "@mui/icons-material/WbSunny";

const dashboardTheme = createTheme(siteTheme, {
  palette: {
    mode: "dark",
    background: {
      default: "#000000",
      paper: "#1a1a1a",
    },
    text: {
      primary: "#ffffff",
      secondary: "#e0e0e0",
    },
  },
});

// Styled Paper for a consistent look
const DashboardItem = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: "center",
  color: theme.palette.text.primary,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "#1a1a1a",
  backgroundImage: "none",
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
}));

// New styled component for squared widgets with specific border radius
const SquaredDashboardItem = styled(DashboardItem)(({ theme }) => ({
  borderRadius: "20px",
  aspectRatio: "1/1",
  minHeight: "150px",
  justifyContent: "space-around",
}));

const weatherIcon = (code: number) => {
  if (code === 0 || code === 1) {
    return WbSunnyIcon;
  }
  if (code === 2) {
    return FilterDramaIcon;
  }
  if (code >= 95) {
    return ThunderstormIcon;
  }
  if (code >= 51 && code <= 57) {
    return GrainIcon;
  }
  if (code >= 61) {
    return UmbrellaIcon;
  }
  return CloudIcon;
};

// --- Helper Components ---

// Current Time and Date Component
const DateTimeDisplay: React.FC = () => {
  const isNarrow = useMediaQuery("(max-width:600px)");
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [weather, setWeather] = useState<{ temp: number; code: number } | null>(
    null,
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadWeather = async () => {
      try {
        const response = await fetch(
          "https://api.open-meteo.com/v1/forecast?latitude=-7.23&longitude=-35.88&current=temperature_2m,weather_code&timezone=America%2FRecife",
        );
        if (!response.ok) {
          return;
        }
        const data = (await response.json()) as {
          current?: { temperature_2m?: number; weather_code?: number };
        };
        const temp = data.current?.temperature_2m;
        const code = data.current?.weather_code;
        if (cancelled || temp === undefined || code === undefined) {
          return;
        }
        setWeather({ temp: Math.round(temp), code });
      } catch (error) {
        console.error("Error fetching weather data:", error);
      }
    };

    loadWeather();
    const timer = window.setInterval(loadWeather, 300000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  const formatDate = (date: Date) => {
    const optionsDate: Intl.DateTimeFormatOptions = isNarrow
      ? { weekday: "short", month: "short", day: "numeric" }
      : {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        };
    const optionsTime: Intl.DateTimeFormatOptions = {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    };
    return {
      date: date.toLocaleDateString("en-US", optionsDate), // Or 'pt-BR' for Portuguese
      time: date.toLocaleTimeString("en-US", optionsTime), // Or 'pt-BR'
    };
  };

  const formatted = formatDate(currentDateTime);
  const WeatherIcon = weather ? weatherIcon(weather.code) : null;

  return (
    <Box sx={{ width: "100%", overflow: "hidden" }}>
      <Typography
        component="h5"
        sx={{
          fontSize: "clamp(1.35rem, 5.5vw, 2.6rem)",
          fontWeight: 600,
          lineHeight: 1.05,
          whiteSpace: "nowrap",
        }}
      >
        {formatted.time}
      </Typography>
      <Typography
        component="h6"
        sx={{
          fontSize: "clamp(0.72rem, 2.1vw, 1.15rem)",
          fontWeight: 500,
          lineHeight: 1.2,
        }}
      >
        {formatted.date}
        {weather && WeatherIcon ? (
          <Box
            component="span"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
            }}
          >
            <WeatherIcon sx={{ fontSize: "1.2em" }} />
            {weather.temp}°C
          </Box>
        ) : null}
      </Typography>
    </Box>
  );
};

const bovespaWidgetUrl = `https://www.tradingview-widget.com/embed-widget/hotlists/?locale=br#${encodeURIComponent(
  JSON.stringify({
    colorTheme: "dark",
    dateRange: "1D",
    exchange: "BMFBOVESPA",
    showChart: false,
    locale: "br",
    width: "100%",
    height: "100%",
    isTransparent: true,
    showSymbolLogo: false,
    showFloatingTooltip: false,
  }),
)}`;

const CALENDAR_TIMEZONE = "America/Recife";

function recifeDateParts(date = new Date()) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: CALENDAR_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const value = (type: string) =>
    Number(parts.find((part) => part.type === type)?.value ?? 0);

  return {
    year: value("year"),
    month: value("month"),
    day: value("day"),
    hour: value("hour") % 24,
    minute: value("minute"),
    second: value("second"),
  };
}

function calendarStamp(year: number, month: number, day: number) {
  const pad = (part: number) => String(part).padStart(2, "0");
  return `${year}${pad(month)}${pad(day)}`;
}

function todayCalendarStamp() {
  const { year, month, day } = recifeDateParts();
  return calendarStamp(year, month, day);
}

const DayCalendar: React.FC = () => {
  const [dayStamp, setDayStamp] = useState(todayCalendarStamp);

  useEffect(() => {
    let timer = 0;
    const scheduleNextDay = () => {
      const { hour, minute, second } = recifeDateParts();
      const elapsed = ((hour * 60 + minute) * 60 + second) * 1000;
      const remaining = 24 * 60 * 60 * 1000 - elapsed + 1000;
      timer = window.setTimeout(() => {
        setDayStamp(todayCalendarStamp());
        scheduleNextDay();
      }, remaining);
    };

    scheduleNextDay();
    return () => window.clearTimeout(timer);
  }, []);

  const src =
    "https://calendar.google.com/calendar/embed?height=600&wkst=1" +
    "&ctz=America%2FRecife&showPrint=0&showTitle=0&showNav=0&showTabs=0" +
    `&mode=AGENDA&dates=${dayStamp}/${dayStamp}` +
    "&bgcolor=%23ffffff&src=ourixilva@gmail.com" +
    "&src=pt.brazilian%23holiday@group.v.calendar.google.com" +
    "&color=%23039be5&color=%237986cb";

  return (
    <iframe
      key={dayStamp}
      title="Calendar"
      src={src}
      style={{
        borderWidth: 0,
        display: "block",
        flex: "1 1 auto",
        minHeight: 0,
        filter: "invert(0.92) hue-rotate(180deg)",
        backgroundColor: "#1a1a1a",
        width: "100%",
        height: "100%",
      }}
    />
  );
};

const BovespaStocks: React.FC = () => {
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        component="iframe"
        title="Bovespa stocks"
        src={bovespaWidgetUrl}
        sx={{
          width: "100%",
          flex: 1,
          minHeight: 0,
          border: 0,
          backgroundColor: "transparent",
        }}
      />
      <Typography
        variant="caption"
        component="a"
        href="https://www.tradingview.com/markets/stocks-brazil/"
        target="_blank"
        rel="noopener noreferrer"
        sx={{ color: "text.secondary", textAlign: "right" }}
      >
        Bovespa by TradingView
      </Typography>
    </Box>
  );
};

// News Ticker Component using react-fast-marquee
interface NewsItem {
  title: string;
  link: string;
  publishedAt: string;
}

const NewsTicker: React.FC = () => {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await fetch(`/api/public/news?t=${Date.now()}`, {
          cache: "no-store",
        });
        if (!response.ok) {
          throw new Error(`News request failed (${response.status})`);
        }
        const fetchedNews = (await response.json()) as NewsItem[];
        setNews(fetchedNews);
        setError(null);
      } catch (err) {
        console.error("Error fetching news:", err);
        setError("Failed to load current news.");
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
    const interval = setInterval(fetchNews, 300000);
    return () => clearInterval(interval);
  }, []);

  if (loading)
    return (
      <Box
        sx={{
          width: "100%",
          textAlign: "center",
          backgroundColor: "#1a1a1a",
        }}
      >
        <Typography variant="body1" color="text.secondary">
          Loading news...
        </Typography>
      </Box>
    );
  if (error)
    return (
      <Box
        sx={{
          width: "100%",
          textAlign: "center",
          backgroundColor: "#1a1a1a",
        }}
      >
        <Typography variant="body1" color="error">
          {error}
        </Typography>
      </Box>
    );
  if (news.length === 0)
    return (
      <Box
        sx={{
          width: "100%",
          textAlign: "center",
          backgroundColor: "#1a1a1a",
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No news available.
        </Typography>
      </Box>
    );

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: "#1a1a1a", // Dark background for ticker
        color: "#fff",
        padding: "4px 0",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <Marquee speed={50} gradient={false} pauseOnHover={true}>
        {news.map((item) => (
          <Typography
            key={`${item.link}-${item.publishedAt}`}
            variant="body1"
            component="span"
            sx={{
              marginRight: "30px",
              fontWeight: "bold",
              fontSize: "clamp(0.75rem, 2.4vw, 1rem)",
              lineHeight: 1.2,
              whiteSpace: "nowrap",
            }}
          >
            <a
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "inherit", textDecoration: "none" }}
            >
              {item.title}
            </a>
          </Typography>
        ))}
      </Marquee>
    </Box>
  );
};

// --- Main Dashboard Component ---

const Dashboard: React.FC = () => {
  useEffect(() => {
    const head = document.head;
    const manifest = document.querySelector<HTMLLinkElement>(
      'link[rel="manifest"]',
    );
    const previousManifest = manifest?.getAttribute("href") ?? null;
    const manifestLink = manifest ?? document.createElement("link");
    const createdManifest = !manifest;
    manifestLink.rel = "manifest";
    manifestLink.href = "/dashboard.webmanifest";
    if (createdManifest) {
      head.appendChild(manifestLink);
    }

    const setMeta = (name: string, content: string) => {
      const existing = document.querySelector<HTMLMetaElement>(
        `meta[name="${name}"]`,
      );
      const tag = existing ?? document.createElement("meta");
      const created = !existing;
      const previous = existing?.getAttribute("content") ?? null;
      tag.name = name;
      tag.content = content;
      if (created) {
        head.appendChild(tag);
      }
      return () => {
        if (created) {
          tag.remove();
        } else if (previous === null) {
          tag.removeAttribute("content");
        } else {
          tag.content = previous;
        }
      };
    };

    const restoreMeta = [
      setMeta("apple-mobile-web-app-capable", "yes"),
      setMeta("mobile-web-app-capable", "yes"),
      setMeta("apple-mobile-web-app-status-bar-style", "black-translucent"),
      setMeta("apple-mobile-web-app-title", "Dashboard"),
      setMeta("theme-color", "#000000"),
    ];

    const viewport = document.querySelector<HTMLMetaElement>(
      'meta[name="viewport"]',
    );
    const previousViewport = viewport?.getAttribute("content") ?? null;
    if (viewport) {
      viewport.content =
        "width=device-width, initial-scale=1, viewport-fit=cover";
    }

    return () => {
      restoreMeta.forEach((restore) => restore());
      if (createdManifest) {
        manifestLink.remove();
      } else if (previousManifest === null) {
        manifestLink.removeAttribute("href");
      } else {
        manifestLink.href = previousManifest;
      }
      if (viewport && previousViewport !== null) {
        viewport.content = previousViewport;
      }
    };
  }, []);

  useEffect(() => {
    const refreshMs = 6 * 60 * 60 * 1000;
    const timer = window.setTimeout(() => {
      window.location.reload();
    }, refreshMs);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <ThemeProvider theme={dashboardTheme}>
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        height: "100dvh",
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
        gridTemplateRows: {
          xs: "minmax(0, 0.7fr) minmax(0, 1.35fr) minmax(0, 1.05fr) minmax(0, 1.05fr) auto",
          sm: "minmax(0, 1.2fr) minmax(0, 1fr) auto",
        },
        gridTemplateAreas: {
          xs: `"clock" "stocks" "calendar" "rates" "news"`,
          sm: `"clock stocks" "calendar rates" "news news"`,
        },
        gap: 0.5,
        width: "100%",
        backgroundColor: "#000",
        overflow: "hidden",
      }}
    >
      <Box sx={{ gridArea: "clock", minHeight: 0 }}>
        <DashboardItem sx={{ p: { xs: 0.5, sm: 1 } }}>
          <DateTimeDisplay />
        </DashboardItem>
      </Box>
      <Box sx={{ gridArea: "stocks", minHeight: 0 }}>
        <DashboardItem sx={{ p: 0.5, alignItems: "stretch" }}>
          <BovespaStocks />
        </DashboardItem>
      </Box>
      <Box sx={{ gridArea: "calendar", minHeight: 0 }}>
        <DashboardItem sx={{ p: 0.5, alignItems: "stretch" }}>
          <DayCalendar />
        </DashboardItem>
      </Box>
      <Box sx={{ gridArea: "rates", minHeight: 0 }}>
        <DashboardItem sx={{ p: 0.5, alignItems: "stretch" }}>
          <iframe
            title="Currency rates"
            src="https://br.widgets.investing.com/live-currency-cross-rates?theme=darkTheme&cols=bid,last,prev,high,low,change,changePerc,time&pairs=2103"
            frameBorder="0"
            style={{
              display: "block",
              flex: "1 1 auto",
              minHeight: 0,
              backgroundColor: "#1a1a1a",
              width: "100%",
              height: "100%",
            }}
          ></iframe>
        </DashboardItem>
      </Box>
      <Box sx={{ gridArea: "news", minHeight: 0 }}>
        <NewsTicker />
      </Box>
    </Box>
    </ThemeProvider>
  );
};

export default Dashboard;
